import type { AgentPageContext } from '@/lib/api/types';

// Mirrors backend/src/api/models/agent.py and schemas/agent.py.
const MAX_ROUTE_LENGTH = 200;
const MAX_PARAMS = 20;
const MAX_PARAM_VALUE_LENGTH = 200;

/**
 * Where the user is, in the shape `POST …/messages` accepts.
 *
 * The backend resolves `/domains/{id}` to the hostname and `/mappings/form?id=`
 * to the mapping's path, tenant-scoped; every other route contributes only
 * itself. Bounds are enforced here as well as there, because a message over any
 * of them is a 422 rather than an answer.
 */
export function pageContextFrom(pathname: string, searchParams: URLSearchParams): AgentPageContext {
  const params: Record<string, string> = {};

  // A dynamic segment is the id the backend looks up, and it is in the path,
  // not the query — so name it explicitly for /domains/<id>.
  const domain = /^\/domains\/([^/]+)/.exec(pathname);
  if (domain) params.id = domain[1];

  for (const [key, value] of Array.from(searchParams.entries()).sort(([a], [b]) => a.localeCompare(b))) {
    if (Object.keys(params).length >= MAX_PARAMS) break;
    params[key.slice(0, MAX_PARAM_VALUE_LENGTH)] = value.slice(0, MAX_PARAM_VALUE_LENGTH);
  }

  return { route: pathname.slice(0, MAX_ROUTE_LENGTH), params };
}

/** Questions worth offering on this page, most specific first. */
export function pageChips(pathname: string): string[] {
  if (/^\/domains\/[^/]+/.test(pathname)) {
    return ['Why isn’t this domain healthy?', 'Which DNS records do I need?'];
  }
  if (pathname.startsWith('/mappings')) {
    return ['Add a rule for mobile visitors'];
  }
  if (pathname.startsWith('/analytics')) {
    return ['Compare this week with last week'];
  }
  if (pathname.startsWith('/email')) {
    return ['Is email forwarding set up correctly?'];
  }
  return [];
}
